// FancyProgress.tsx
import React, { useEffect, useMemo, useRef, useState } from 'react';
import clsx from 'clsx';
import PropTypes from 'prop-types';

// ...

function ProgressBar({
  remaining,
  max,
  unit = '',
  startLabel,
  endLabel,
  showSummary = true,
  tooltipOffset = 36,
  className,
  animate = true,
  showTooltip = true,
  showPoints = true,
  fillDuration = 600,
  easing = 'cubic-bezier(0.22, 1, 0.36, 1)',
}) {
  const achieved = Math.min(Math.max(max - (remaining ?? 0), 0), max);
  const pct = max > 0 ? (achieved / max) * 100 : 0;

  const [internalPct, setInternalPct] = useState(0);
  const [transitionOn, setTransitionOn] = useState(true);
  const raf1 = useRef(null);
  const raf2 = useRef(null);

  const prefersReducedMotion = useMemo(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return false;
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }, []);

  // 🔁 Всегда перезапускаем анимацию при изменении цели (pct)
  useEffect(() => {
    // Без анимации — просто ставим значение
    if (!animate || prefersReducedMotion) {
      setTransitionOn(false);
      setInternalPct(pct);
      return;
    }

    // 1) Сбрасываем до 0 без transition
    setTransitionOn(false);
    setInternalPct(0);

    // 2) На следующий кадр включаем transition…
    raf1.current = requestAnimationFrame(() => {
      setTransitionOn(true);
      // 3) …и ещё через кадр ставим целевой процент — поедем из 0 → pct
      raf2.current = requestAnimationFrame(() => {
        setInternalPct(pct);
      });
    });

    return () => {
      if (raf1.current) cancelAnimationFrame(raf1.current);
      if (raf2.current) cancelAnimationFrame(raf2.current);
    };
  }, [pct, animate, prefersReducedMotion]);

  const transitionWidth = transitionOn
    ? `width ${fillDuration}ms ${easing}`
    : 'none';
  const transitionLeft = transitionOn
    ? `left ${fillDuration}ms ${easing}`
    : 'none';

  const alignLeft = pct <= 8;
  const alignRight = pct >= 92;
  const tipLeft = alignLeft ? '0%' : alignRight ? '100%' : `${internalPct}%`;
  const tipTranslate = alignLeft
    ? 'translateX(0)'
    : alignRight
      ? 'translateX(-100%)'
      : 'translateX(-50%)';

  return (
    <div className="w-full">
      <div
        className={clsx(
          'relative h-3.5 w-full overflow-visible rounded-full ring-opacity-10',
          className,
        )}
        style={{
          ['--tw-ring-color']: 'var(--color-event-light)',
          background:
            'color-mix(in srgb, var(--color-event-bright) 28%, transparent)',
        }}
      >
        {/* Заполнение */}
        <div
          className="h-full rounded-full shadow-[inset_0_1px_2px_rgba(255,255,255,.15)]"
          style={{
            width: `${internalPct}%`,
            transition: transitionWidth,
            background:
              'linear-gradient(90deg, var(--color-event-primary) 0%, var(--color-event-bright) 45%, var(--color-event-accent) 100%)',
          }}
          role="progressbar"
          aria-valuemin={0}
          aria-valuemax={max}
          aria-valuenow={achieved}
        />

        {/* Искра */}
        <div
          className="pointer-events-none absolute top-1/2 -translate-y-1/2"
          style={{
            left: `calc(${internalPct}% - 6px)`,
            transition: transitionLeft,
          }}
        >
          <div
            className="h-4 w-4 rounded-full"
            style={{
              background: 'var(--color-event-light)',
              boxShadow:
                '0 0 12px color-mix(in srgb, var(--color-event-bright) 70%, transparent)',
            }}
          />
        </div>

        {/* Тултип */}
        {showTooltip && (
          <div
            className="pointer-events-none absolute"
            style={{
              left: tipLeft,
              transform: tipTranslate,
              top: `-${tooltipOffset}px`,
              transition: transitionLeft,
            }}
          >
            <div
              className="min-w-[140px] max-w-[260px] rounded-xl px-3 py-1.5 text-xs font-medium shadow-lg ring-1 whitespace-nowrap"
              style={{
                background:
                  'color-mix(in srgb, var(--color-event-deep) 92%, transparent)',
                color: 'var(--color-event-light)',
                ['--tw-ring-color']: 'var(--color-event-light)',
                boxShadow:
                  '0 6px 18px color-mix(in srgb, var(--color-event-deep) 70%, transparent)',
              }}
            >
              осталось {Math.max(remaining, 0)}
              {unit}
            </div>
            <div
              className="relative -mt-1 h-2 w-2 rotate-45 ring-1"
              style={{
                background:
                  'color-mix(in srgb, var(--color-event-deep) 92%, transparent)',
                ['--tw-ring-color']: 'var(--color-event-light)',
                left: alignLeft
                  ? '12px'
                  : alignRight
                    ? 'calc(100% - 12px)'
                    : '50%',
                transform: alignLeft
                  ? 'translateX(0) rotate(45deg)'
                  : alignRight
                    ? 'translateX(-100%) rotate(45deg)'
                    : 'translateX(-50%) rotate(45deg)',
              }}
            />
          </div>
        )}
      </div>

      {showPoints && (
        <div
          className="mt-1 flex items-center justify-between text-xs"
          style={{
            color:
              'color-mix(in srgb, var(--color-event-text) 60%, transparent)',
          }}
        >
          <span>{startLabel ?? <>0{unit}</>}</span>
          <span>
            {endLabel ?? (
              <>
                {max}
                {unit}
              </>
            )}
          </span>
        </div>
      )}

      {showSummary && (
        <div
          className="mt-0.5 flex items-center justify-between text-[11px]"
          style={{
            color:
              'color-mix(in srgb, var(--color-event-text) 55%, transparent)',
          }}
        >
          <span className="tabular-nums">
            прогресс: {achieved}
            {unit} / {max}
            {unit}
          </span>
          <span className="font-mono">{Math.round(pct)}%</span>
        </div>
      )}
    </div>
  );
}

ProgressBar.propTypes = {
  remaining: PropTypes.number.isRequired,
  max: PropTypes.number.isRequired,
  unit: PropTypes.string,
  startLabel: PropTypes.node,
  endLabel: PropTypes.node,
  showSummary: PropTypes.bool,
  className: PropTypes.string,
  animate: PropTypes.bool,
  fillDuration: PropTypes.number,
  easing: PropTypes.string,
};

export default ProgressBar;
